import {
  ExpandableSearch,
  FilterPill,
  ListRow,
  PresenceAvatar,
} from "@somethings-ui/web";
import { useMemo, useState } from "react";
import { formatRelativeNoticeTimestamp } from "@/components/mentor-workflows/notice/notice-timestamp";
import type { TrainingConversationBucket } from "@/lib/api/training-simulator/training-simulator";
import { cn } from "@/lib/utils.ts";

export interface TrainingThreadListItem {
  id: string;
  name: string;
  preview: string;
  lastMessageAt: string | null;
  unreadCount: number;
  bucket: TrainingConversationBucket | null;
  online: boolean;
}

interface TrainingSessionThreadListProps {
  items: TrainingThreadListItem[];
  activeThreadId: string | undefined;
  onSelect: (threadId: string) => void;
}

type SimulatorListFilter =
  | "all"
  | "needs_response"
  | "unread"
  | "online"
  | "low_engagement"
  | "new";

const FILTERS: { id: SimulatorListFilter; label: (count: number) => string }[] =
  [
    { id: "all", label: () => "All" },
    {
      id: "needs_response",
      label: (count) => `${count} needs response`,
    },
    { id: "unread", label: (count) => `${count} unread` },
    { id: "online", label: (count) => `${count} online` },
    {
      id: "low_engagement",
      label: (count) => `${count} low engagement`,
    },
    { id: "new", label: (count) => `${count} new` },
  ];

function matchesFilter(
  item: TrainingThreadListItem,
  filter: SimulatorListFilter,
) {
  if (filter === "all") return true;
  if (filter === "unread") return item.unreadCount > 0;
  return item.bucket === filter;
}

function rowLabel(item: TrainingThreadListItem) {
  if (item.unreadCount <= 0) return item.name;
  const noun = item.unreadCount === 1 ? "message" : "messages";
  return `${item.name}, ${item.unreadCount} unread ${noun}`;
}

export function TrainingSessionThreadList({
  items,
  activeThreadId,
  onSelect,
}: TrainingSessionThreadListProps) {
  const [filter, setFilter] = useState<SimulatorListFilter>("all");
  const [search, setSearch] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);

  const counts = useMemo(() => {
    const tally: Record<SimulatorListFilter, number> = {
      all: items.length,
      needs_response: 0,
      unread: 0,
      online: 0,
      low_engagement: 0,
      new: 0,
    };
    for (const item of items) {
      if (item.unreadCount > 0) tally.unread += 1;
      if (item.bucket && item.bucket in tally) {
        tally[item.bucket] += 1;
      }
    }
    return tally;
  }, [items]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (!matchesFilter(item, filter)) return false;
      if (!query) return true;
      return item.name.toLowerCase().includes(query);
    });
  }, [filter, items, search]);

  return (
    <aside className="flex h-full w-[300px] shrink-0 flex-col bg-surfaces-z3 pt-4">
      <header className="relative flex h-14 shrink-0 items-center border-b border-surfaces-z4 px-3">
        <span
          className={`truncate py-1 pl-2 font-heading text-header-4 text-text-primary transition-opacity duration-150${searchExpanded ? " opacity-0" : ""}`}
        >
          Teens
        </span>
        <ExpandableSearch
          value={search}
          onChange={setSearch}
          expanded={searchExpanded}
          onExpandedChange={setSearchExpanded}
          placeholder="Search teens"
          className={cn(
            "absolute top-1/2 right-3 -translate-y-1/2",
            searchExpanded ? "w-[calc(100%-1.5rem)]" : "w-10",
          )}
        />
      </header>

      <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-surfaces-divider px-4 pb-[13px] pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {FILTERS.map((entry) => (
          <FilterPill
            key={entry.id}
            label={entry.label(counts[entry.id])}
            active={filter === entry.id}
            onClick={() => setFilter(entry.id)}
          />
        ))}
      </div>

      <div className="subtle-scrollbar flex-1 overflow-y-auto px-2 py-2">
        {visibleItems.length === 0 ? (
          <p className="px-3 py-6 text-body-3 text-text-secondary">
            No teens in this view.
          </p>
        ) : (
          visibleItems.map((item) => (
            <ListRow
              key={item.id}
              selected={item.id === activeThreadId}
              unread={item.unreadCount > 0}
              onClick={() => onSelect(item.id)}
              aria-label={rowLabel(item)}
              className={cn(
                "min-h-[64px]",
                item.id === activeThreadId
                  ? undefined
                  : "hover:bg-surfaces-z4",
              )}
            >
              <ListRow.Leading>
                <PresenceAvatar
                  online={item.online}
                  name={item.name}
                  size="sm"
                />
              </ListRow.Leading>
              <ListRow.Content>
                <ListRow.Title>{item.name}</ListRow.Title>
                <ListRow.Subtitle>
                  {item.preview || "No messages yet"}
                </ListRow.Subtitle>
              </ListRow.Content>
              <ListRow.Trailing>
                {item.lastMessageAt ? (
                  <span className="pt-0.5 font-body text-body-3 leading-[1.4] text-text-secondary">
                    {formatRelativeNoticeTimestamp(item.lastMessageAt)}
                  </span>
                ) : null}
              </ListRow.Trailing>
            </ListRow>
          ))
        )}
      </div>
    </aside>
  );
}
